/**
 * **Fundstellen auf dem Original** (Auftraggeber 13.10.2026): Wo auf der Seite steht, was gelesen wurde?
 *
 * Die KI liefert zu ihren Werten ein wörtliches Zitat und die Seite, aber keine Koordinaten. Die hat die
 * Textebene der PDF: Jedes Textstück trägt sein Rechteck. Hier wird das Zitat in den Textstücken der Seite
 * gesucht — ohne Datenbank, ohne pdf.js, prüfbar mit ein paar Rechtecken.
 *
 * Ein Scan oder Foto hat keine Textebene und damit keine Fundstellen; das ist kein Fehler dieser Suche.
 */

/** Ein Textstück der Seite mit seinem Rechteck — Einheiten der Seite, Ursprung oben links. */
export interface Textstueck {
  text: string
  x: number
  y: number
  w: number
  h: number
}

/** Was markiert werden soll: das, was zu einem Feld oder einer Zeile gelesen wurde. */
export interface Fundstelle {
  /** Kennung des Felds oder der Zeile — dieselbe, mit der die Maske ihre Zeile kennt */
  id: string
  /** Seite ab 1; ohne Angabe wird auf jeder Seite gesucht */
  seite?: number | null
  /** wörtliche Ausschnitte, wie sie auf der Seite stehen — jeder wird für sich gesucht */
  texte: readonly (string | null | undefined)[]
  /** Steht ein Ausschnitt mehrfach auf der Seite (ein Betrag), zählt das Vorkommen, das der Zeile mit diesem Text am nächsten ist */
  anker?: string | null
  /** Hinweis beim Zeigen auf die Markierung */
  titel?: string
}

export interface Marke {
  id: string
  x: number
  y: number
  w: number
  h: number
  titel?: string
}

/** Unter so vielen Zeichen ist ein Ausschnitt zu beliebig, um ihn zu suchen („20", „m"). */
const MIN_ZEICHEN = 3
/** Ohne Anker: höchstens so viele Vorkommen desselben Ausschnitts je Seite. */
const MAX_VORKOMMEN = 4
/** Eine Zeile gilt als die gesuchte, wenn so viel vom Ausschnitt in ihr steht. */
const MIN_DECKUNG = 0.6

/**
 * Der Kern eines Texts für den Vergleich: klein, ohne Leerraum, Satzzeichen und Akzente; Ligaturen
 * aufgelöst. `1.234,50`, `1 234,50` und `1234,50` sind danach dasselbe — für eine Markierung ist das
 * genau genug, eine Prüfung ist es nicht (die macht der Server, `zitatGefunden`).
 */
export const kern = (text: string): string =>
  text
    .normalize('NFKD')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]/gu, '')

interface Rechteck {
  x: number
  y: number
  w: number
  h: number
}

/** Die Stücke in Zeilen, jede von links nach rechts — pdf.js liefert sie nicht immer in Lesereihenfolge. */
export function zeilenAus(stuecke: readonly Textstueck[]): number[][] {
  const folge = stuecke.map((_, i) => i).filter((i) => stuecke[i].text.trim() !== '')
  folge.sort((a, b) => stuecke[a].y + stuecke[a].h / 2 - (stuecke[b].y + stuecke[b].h / 2))
  const zeilen: number[][] = []
  let mitte = Number.NaN
  for (const i of folge) {
    const s = stuecke[i]
    const m = s.y + s.h / 2
    // Dieselbe Zeile, solange die Mitte um weniger als eine halbe Schrifthöhe abweicht.
    if (zeilen.length && Math.abs(m - mitte) < Math.max(2, s.h * 0.5)) zeilen[zeilen.length - 1].push(i)
    else {
      zeilen.push([i])
      mitte = m
    }
  }
  for (const z of zeilen) z.sort((a, b) => stuecke[a].x - stuecke[b].x)
  return zeilen
}

/** Der Kern mehrerer Stücke am Stück — und zu jedem Zeichen, aus welchem Stück und von welcher Stelle es kommt. */
function strom(stuecke: readonly Textstueck[], folge: readonly number[]) {
  let text = ''
  const stueck: number[] = []
  const stelle: number[] = []
  for (const i of folge) {
    const roh = stuecke[i].text
    for (let p = 0; p < roh.length; p++) {
      for (const z of kern(roh[p])) {
        text += z
        stueck.push(i)
        stelle.push(p)
      }
    }
  }
  return { text, stueck, stelle }
}

/** Der Teil eines Stücks zwischen zwei Zeichen — die Breite geht anteilig, genauer weiß es nur die Schrift. */
function teil(s: Textstueck, von: number, bis: number): Rechteck {
  const n = Math.max(1, s.text.length)
  return { x: s.x + (s.w * von) / n, y: s.y, w: (s.w * (bis - von)) / n, h: s.h }
}

/** Rechtecke derselben Zeile, die aneinanderstoßen, werden eines. */
function vereine(rechtecke: Rechteck[]): Rechteck[] {
  const sortiert = [...rechtecke].sort((a, b) => (Math.abs(a.y - b.y) < Math.max(2, a.h * 0.5) ? a.x - b.x : a.y - b.y))
  const aus: Rechteck[] = []
  for (const r of sortiert) {
    const l = aus[aus.length - 1]
    if (l && Math.abs(l.y - r.y) < Math.max(2, l.h * 0.5) && r.x - (l.x + l.w) < Math.max(l.h, r.h) * 1.5) {
      const rechts = Math.max(l.x + l.w, r.x + r.w)
      const unten = Math.max(l.y + l.h, r.y + r.h)
      l.x = Math.min(l.x, r.x)
      l.y = Math.min(l.y, r.y)
      l.w = rechts - l.x
      l.h = unten - l.y
    } else aus.push({ ...r })
  }
  return aus
}

interface Vorkommen {
  rechtecke: Rechteck[]
  /** senkrechte Mitte — zum Vergleich mit dem Anker */
  mitte: number
}

const mitteVon = (r: readonly Rechteck[]) => (r.length ? r.reduce((s, x) => s + x.y + x.h / 2, 0) / r.length : 0)

/** Eine Seite, einmal vorbereitet: ihre Zeilen, deren Kern und der Kern der ganzen Seite am Stück. */
interface Seite {
  stuecke: readonly Textstueck[]
  zeilen: number[][]
  zeilenKern: string[]
  strom: ReturnType<typeof strom>
}

function bereite(stuecke: readonly Textstueck[]): Seite {
  const zeilen = zeilenAus(stuecke)
  return { stuecke, zeilen, zeilenKern: zeilen.map((z) => kern(z.map((i) => stuecke[i].text).join(''))), strom: strom(stuecke, zeilen.flat()) }
}

/** Jedes Vorkommen des Ausschnitts im Text der Seite, wörtlich (im Kern) und über Stückgrenzen hinweg. */
function woertlich({ stuecke, strom: s }: Seite, gesucht: string): Vorkommen[] {
  const aus: Vorkommen[] = []
  for (let ab = s.text.indexOf(gesucht); ab !== -1 && aus.length < 50; ab = s.text.indexOf(gesucht, ab + gesucht.length)) {
    const jeStueck = new Map<number, { von: number; bis: number }>()
    for (let k = ab; k < ab + gesucht.length; k++) {
      const b = jeStueck.get(s.stueck[k])
      if (b) b.bis = Math.max(b.bis, s.stelle[k] + 1)
      else jeStueck.set(s.stueck[k], { von: s.stelle[k], bis: s.stelle[k] + 1 })
    }
    const rechtecke = vereine([...jeStueck].map(([i, b]) => teil(stuecke[i], b.von, b.bis)))
    aus.push({ rechtecke, mitte: mitteVon(rechtecke) })
  }
  return aus
}

/**
 * Die Zeile, in der am meisten vom Ausschnitt steht — für Zitate, die die KI leicht anders geschrieben hat,
 * und für den Anker (die Bezeichnung einer Position). Markiert werden die Stücke, in denen ein Wort des
 * Ausschnitts steht.
 */
function besteZeile({ stuecke, zeilen, zeilenKern }: Seite, ausschnitt: string): Vorkommen | null {
  const worte = [...new Set(ausschnitt.split(/\s+/).map(kern).filter((w) => w.length >= 2))]
  const gesamt = worte.reduce((s, w) => s + w.length, 0)
  if (gesamt < MIN_ZEICHEN) return null
  let beste: { deckung: number; zeile: number[]; treffer: string[] } | null = null
  for (let z = 0; z < zeilen.length; z++) {
    const zeile = zeilen[z]
    const text = zeilenKern[z]
    const treffer = worte.filter((w) => text.includes(w))
    const deckung = treffer.reduce((s, w) => s + w.length, 0) / gesamt
    if (deckung > (beste?.deckung ?? 0)) beste = { deckung, zeile, treffer }
  }
  if (!beste || beste.deckung < MIN_DECKUNG || beste.treffer.reduce((s, w) => s + w.length, 0) < MIN_ZEICHEN + 1) return null
  const dabei = beste.zeile.filter((i) => {
    const k = kern(stuecke[i].text)
    // Ein Stück gehört dazu, wenn ein Wort in ihm steht — oder es selbst Teil eines Worts ist. Das zweite nur ab drei
    // Zeichen: Die Positionsnummer „1" steckt in jedem „100×100" und gehört trotzdem nicht zur Bezeichnung.
    return k.length > 0 && beste!.treffer.some((w) => k.includes(w) || (k.length >= MIN_ZEICHEN && w.includes(k)))
  })
  const rechtecke = vereine((dabei.length ? dabei : beste.zeile).map((i) => ({ x: stuecke[i].x, y: stuecke[i].y, w: stuecke[i].w, h: stuecke[i].h })))
  return { rechtecke, mitte: mitteVon(rechtecke) }
}

/**
 * Die Markierungen einer Seite. Je Fundstelle: jeder Ausschnitt wörtlich, sonst die Zeile, die ihm am
 * nächsten kommt; mit Anker nur das Vorkommen bei dessen Zeile, und der Anker selbst wird mitmarkiert.
 */
export function findeMarken(stuecke: readonly Textstueck[], fundstellen: readonly Fundstelle[]): Marke[] {
  if (!stuecke.length || !fundstellen.length) return []
  const seite = bereite(stuecke)
  const marken: Marke[] = []
  for (const f of fundstellen) {
    const anker = f.anker?.trim() ? besteZeile(seite, f.anker) : null
    const rechtecke: Rechteck[] = []
    for (const roh of f.texte) {
      const gesucht = kern(roh ?? '')
      if (gesucht.length < MIN_ZEICHEN) continue
      let treffer = woertlich(seite, gesucht)
      if (!treffer.length) {
        const zeile = besteZeile(seite, roh ?? '')
        if (zeile) treffer = [zeile]
      }
      if (anker && treffer.length > 1) treffer = [treffer.reduce((a, b) => (Math.abs(b.mitte - anker.mitte) < Math.abs(a.mitte - anker.mitte) ? b : a))]
      for (const t of treffer.slice(0, MAX_VORKOMMEN)) rechtecke.push(...t.rechtecke)
    }
    if (anker) rechtecke.push(...anker.rechtecke)
    for (const r of vereine(rechtecke)) marken.push({ id: f.id, ...r, ...(f.titel ? { titel: f.titel } : {}) })
  }
  return marken
}

// ── Werte ohne Zitat: wie ein Datum oder ein Betrag auf dem Papier stehen kann ─────────────

/** Ein Tag `JJJJ-MM-TT` in den Schreibweisen, die auf Geschäftspapier vorkommen. */
export function datumSchreibweisen(tag: string | null | undefined): string[] {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(tag ?? '')
  if (!m) return []
  const [, j, mo, t] = m
  const kurz = (x: string) => String(Number(x))
  // Im Kern fallen Punkte, Striche und Schrägstriche weg: „12.02.2026" und „12/02/2026" sind dasselbe.
  return [...new Set([`${t}.${mo}.${j}`, `${kurz(t)}.${kurz(mo)}.${j}`, `${t}.${mo}.${j.slice(2)}`, `${j}-${mo}-${t}`])]
}
